import { BreadcrumbTrail } from '@shared/ui';
import { type FC, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getMemberDisplayName } from '../../features/members/types';
import { useGetTeamQuery } from '../../store/api/apiSlice';
import { useBreadcrumbOverrides } from './BreadcrumbOverridesContext';
import { type BreadcrumbMemberContext, buildNonAdminBreadcrumbs } from './nonAdminBreadcrumbModel';

type NonAdminBreadcrumbsProps = {
  viewerMode?: 'coach' | 'member';
};

const NonAdminBreadcrumbs: FC<NonAdminBreadcrumbsProps> = ({ viewerMode = 'coach' }) => {
  const location = useLocation();
  const params = useParams();
  const { currentOverrides } = useBreadcrumbOverrides();
  const { data: teamData } = useGetTeamQuery(undefined, { skip: viewerMode === 'member' });

  const routeMemberId =
    params.personId ?? params.memberId ?? currentOverrides?.memberContext?.memberId;
  const routeMemberLabel = useMemo(() => {
    if (!routeMemberId) {
      return undefined;
    }
    const member = teamData?.members?.find((candidate) => candidate.person.id === routeMemberId);
    if (!member) {
      return undefined;
    }
    return getMemberDisplayName(member);
  }, [routeMemberId, teamData?.members]);

  const fallbackMemberContext: BreadcrumbMemberContext | null = routeMemberId
    ? {
        memberId: routeMemberId,
        memberLabel: routeMemberLabel,
      }
    : null;
  const mergedMemberContext: BreadcrumbMemberContext | null =
    currentOverrides?.memberContext || fallbackMemberContext
      ? {
          memberId: currentOverrides?.memberContext?.memberId ?? fallbackMemberContext?.memberId,
          memberLabel:
            currentOverrides?.memberContext?.memberLabel ?? fallbackMemberContext?.memberLabel,
        }
      : null;

  const model = buildNonAdminBreadcrumbs({
    labels: currentOverrides?.labels,
    memberContext: mergedMemberContext,
    pathname: location.pathname,
    viewerMode,
  });

  if (!model || model.items.length === 0) {
    return null;
  }

  return <BreadcrumbTrail items={model.items} />;
};

export default NonAdminBreadcrumbs;
